import { useCallback, useContext, useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useForm, FormProvider } from "react-hook-form";
import { MainTemplate } from "@/components/templates";
import { useSnackBar } from "@moneyforward/mfx_biz_react_components";
import { useRegisterMemberWithBizsolMutation } from "@/generated/graphql";
import {
  BizsolRegisterUserFormFew,
  BizsolRegisterUserForm,
} from "@/components/organisms";
import { AuthContext, AuthStatus, SessionInfo } from "@/contexts/AuthContext";
import useFixedAuth from "@/libs/useFixedAuth";
import { PageTitle } from "@/components/molecules";
import { getUrlWithQuery } from "@/libs/url";
import {
  ErrorCode,
  errorCodeIsIncluded,
  getErrorMessage,
  moveToErrorPage,
} from "@/libs/errors";
import { ApolloError } from "@apollo/client";
import config from "@/libs/config";
import { setLoginSource, LoginSource } from "@/libs/loginSource";
import { useAmIContractAdmin } from "@/components/hooks/useAmIContractAdmin";
import { useUpdateContractAdminStatus } from "@/components/hooks/useUpdateContractAdminStatus";
import useRollbarUtil from "../hooks/useRollbarUtil";
import Rollbar from "rollbar";

type FormValues = {
  lastName: string;
  lastNameKana: string;
  middleName: string;
  middleNameKana: string;
  firstName: string;
  firstNameKana: string;
  email?: string;
  password?: string;
};

export const BizsolRegisterUserPage = () => {
  const { rollbar } = useRollbarUtil();
  const { setSignInUserSession, userInfo } = useContext(AuthContext);
  const { authStatus } = useFixedAuth();
  const { notify } = useSnackBar();
  const syncWithBizsolEmail = config.IS_SHIKOKUBANK; // Bizsolに登録しているEmailアドレスをMikatano IDでも利用する
  const shouldCheckContractAdminStatus = config.IS_SHIKOKUBANK; // ログイン時に必要に応じて契約管理者権限の付与・剥奪をする

  const [calledSetSignInUserSession, setCalledSetSignInUserSession] = useState<
    boolean | null
  >(null);
  const [amIContractAdmin, actionAmIContractAdmin] = useAmIContractAdmin();
  const [calledUpdateContractAdminStatus, setCalledUpdateContractAdminStatus] =
    useState<boolean | null>(null);
  const {
    loading: loadingUpdateContractAdminStatus,
    error: errorUpdateContractAdminStatus,
    result: resultUpdateContractAdminStatus,
    actionUpdateContractAdminStatus,
  } = useUpdateContractAdminStatus();

  const router = useRouter();
  if (!router.isReady) {
    return <div></div>;
  }
  const { app, code } = router.query;
  const registrationCode = Array.isArray(code) ? code[0] : code;

  // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: fix to comply with the rules of react-hooks as not following them may lead to unintended side effects
  const methods = useForm<FormValues>({
    reValidateMode: "onBlur",
    defaultValues: {
      middleName: "",
      middleNameKana: "",
    },
  });

  const signIn = async (token: {
    access_token: string;
    id_token: string;
    refresh_token: string;
    token_type: string;
  }) => {
    try {
      const session: SessionInfo = {
        accessToken: token.access_token,
        idToken: token.id_token,
        refreshToken: token.refresh_token,
      };
      await setSignInUserSession(session);
      setLoginSource(LoginSource.bizsol);
      setCalledSetSignInUserSession(true); // ログインセッション更新後の処理を駆動する
      notify("ログインが完了しました");
    } catch {
      rollbar.error("Failed to set session");
      notify("ログインに失敗しました");
      setLoginSource(LoginSource.none);
      moveToErrorPage(ErrorCode.BIZSOL_INTERNAL_SERVER_ERROR);
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: fix to comply with the rules of react-hooks as not following them may lead to unintended side effects
  const [registerMember] = useRegisterMemberWithBizsolMutation({
    onCompleted: (data) => {
      const token = data.registerMemberWithBizsol.token;
      if (!token) {
        moveToErrorPage(ErrorCode.BIZSOL_INTERNAL_SERVER_ERROR);
        return;
      }
      signIn(token);
    },
    onError: (err) => {
      // snackbar で表示すべきエラーの場合は、ここで処理を終える
      // snackbar で表示すべきエラーでない場合は、handleError に処理を委譲する
      const defaultMessage = "新規登録に失敗しました";
      const message = getErrorMessage(err, defaultMessage);
      const isSnackbarError = message !== defaultMessage;
      if (isSnackbarError) {
        notify(message);
        return;
      } else {
        handleError(err, {
          rollbar,
          message: "Failed to register member with bizsol",
        });
      }
    },
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: fix to comply with the rules of react-hooks as not following them may lead to unintended side effects
  const handleSubmit = useCallback(
    async (v: FormValues) => {
      const client_id = config.COGNITO_CLIENT_ID;
      await registerMember({
        variables: {
          input: {
            client_id,
            registrationCode,
            ...v,
          },
        },
      });
    },
    [registerMember]
  );

  // After sign-in, Update ContractAdmin Status and go to home
  // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: fix to comply with the rules of react-hooks as not following them may lead to unintended side effects
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    // signIn 完了後にしか実行しない
    if (authStatus != AuthStatus.SignedIn) {
      return;
    }

    if (!shouldCheckContractAdminStatus) {
      // サービスページに遷移する
      router.push(getUrlWithQuery("/", app));
    }

    // [四国銀行の特別対応]
    // - 四国銀行は、ログイン時にBiz権限とMikatano ID権限を同期させる特殊仕様がある
    // - その影響で、通常のフローだと契約管理者が0人になる恐れがある
    // - それを抑止するため、特別に「Mi管理者として登録されたならば無条件で契約管理者権限も付与」という仕様になった
    // - 尚、 `bizsol/register-company` 時は、通常Mikatano動線と同様で、
    //   IDバックエンド処理内で呼ばれる課金基盤の事業者作成時に契約管理者権限が付与されるので、IDフロントエンドで特に対応は必要ない
    //
    // [Note]
    // registerMember.onCompleted -> signIn()
    // 内で setSignInUserSession が呼ばれ、userInfo が非同期で更新される
    // (厳密には setSignInUserSession -> authStatus update -> userInfo update という流れ at AuthProvider)
    // userInfo は、未ログイン状態 undefined --> ログイン状態に変化することが期待される
    // 本処理は、 setSignInUserSession 実行後のレンダリングで呼び出されるが、
    // userInfo の更新までには時間差がある想定なので、userInfo の更新完了後に諸々の処理を実行する

    // userInfo にログインしたユーザーの情報が格納されていなければ実行しない
    if (!userInfo || !userInfo.sub) {
      return;
    }

    // 現在の契約管理者権限が未取得であれば取得する
    if (amIContractAdmin == null) {
      actionAmIContractAdmin(userInfo.sub);
      return;
    }

    // Mikatano管理者ならば契約管理者権限を付与する
    // 必要がなければ、何もしない
    // 1度だけ実行
    if (!calledUpdateContractAdminStatus) {
      actionUpdateContractAdminStatus(
        userInfo.sub,
        userInfo.authority,
        amIContractAdmin
      );
      setCalledUpdateContractAdminStatus(true);
      return;
    }

    // actionUpdateContractAdminStatus が完了していなければ待機
    if (loadingUpdateContractAdminStatus == true) {
      return;
    }

    // actionUpdateContractAdminStatus が完了してエラーならばエラーメッセージを表示
    if (
      loadingUpdateContractAdminStatus == false &&
      errorUpdateContractAdminStatus
    ) {
      handleError(errorUpdateContractAdminStatus, {
        rollbar,
        message: "Failed to update contract admin status",
      });
    }

    // actionUpdateContractAdminStatus が完了して正常終了ならば次の画面に遷移
    if (
      loadingUpdateContractAdminStatus == false &&
      resultUpdateContractAdminStatus
    ) {
      // 次のページに遷移する
      router.push(getUrlWithQuery("/", app));
    }
  }, [
    calledSetSignInUserSession,
    userInfo,
    amIContractAdmin,
    actionUpdateContractAdminStatus,
    calledUpdateContractAdminStatus,
    loadingUpdateContractAdminStatus,
    errorUpdateContractAdminStatus,
    resultUpdateContractAdminStatus,
  ]);

  return (
    <MainTemplate title={`新規登録`} disableAccountDropDown={true}>
      <>
        <PageTitle
          mainTitle={`新規登録`}
          subTitle={
            <>
              ユーザー情報を入力して利用規約に同意し
              <br />
              登録を開始してください
            </>
          }
        />
        <FormProvider {...methods}>
          {syncWithBizsolEmail ? (
            <BizsolRegisterUserFormFew onSubmit={handleSubmit} />
          ) : (
            <BizsolRegisterUserForm
              onSubmit={handleSubmit}
              setValue={methods.setValue}
            />
          )}
        </FormProvider>
      </>
    </MainTemplate>
  );
};

const handleError = (
  err: ApolloError,
  rollbarInfo: { rollbar: Rollbar; message: string }
) => {
  const { rollbar, message: messageToRollbar } = rollbarInfo;
  const errorList: Array<ErrorCode> = [
    // FIXME: 他のパターンがないか確認する
    ErrorCode.BIZSOL_INVALID_REGISTRATION_TOKEN,
  ];

  errorList.forEach((code) => {
    if (errorCodeIsIncluded(err, code)) {
      moveToErrorPage(code);
      return;
    }
  });

  // 上記にないエラーでもbizsol ボタンのあるエラーページに遷移する
  // username はランダム発番するため、USERNAME_EXISTS はInternalError として扱う
  rollbar.error(messageToRollbar, err);
  moveToErrorPage(ErrorCode.BIZSOL_INTERNAL_SERVER_ERROR);
};
